import { useState } from "react";

type SortValue = string | number | null | undefined;
const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

/** Unknowns stay last in either direction; equal values retain input order. */
export function sortAnalyticsRows<T>(rows: T[], value: (row: T) => SortValue, direction: "asc" | "desc"): T[] {
  const clean = (v: SortValue) => v == null || v === "" || (typeof v === "number" && !Number.isFinite(v)) ? null : v;
  return rows.map((row, index) => ({ row, index })).sort((a, b) => {
    const av = clean(value(a.row));
    const bv = clean(value(b.row));
    if (av == null || bv == null) return av == null && bv == null ? a.index - b.index : av == null ? 1 : -1;
    const comparison = typeof av === "number" && typeof bv === "number" ? av - bv : collator.compare(String(av), String(bv));
    return (direction === "asc" ? comparison : -comparison) || a.index - b.index;
  }).map(({ row }) => row);
}

// Ascending tier = least to most balance risk; trend = worsening, stable, improving.
export const memberTierRank = { green: 0, yellow: 1, orange: 2, red: 3 };
export const memberTrendRank = { worsening: 0, stable: 1, improving: 2 };

/** Chronological endpoints, not API array order. A single month cannot prove a trend. */
export function roomTrendDelta(history: { month: string; revpar: number }[]): number | null {
  const points = history.filter((p) => Number.isFinite(p.revpar)).slice().sort((a, b) => a.month.localeCompare(b.month));
  if (points.length < 2 || points[0].month === points[points.length - 1].month) return null;
  return points[points.length - 1].revpar - points[0].revpar;
}

export function useAnalyticsSort<K extends string>(initialField: K | null, initialDirection: "asc" | "desc" = "asc") {
  const [field, setField] = useState<K | null>(initialField);
  const [direction, setDirection] = useState(initialDirection);
  const toggle = (key: K) => {
    if (key === field) setDirection((d) => d === "asc" ? "desc" : "asc");
    else { setField(key); setDirection("asc"); }
  };
  return { field, direction, toggle };
}

export function AnalyticsSortHeader<K extends string>({ label, column, sort }: {
  label: string; column: K; sort: ReturnType<typeof useAnalyticsSort<K>>;
}) {
  const selected = column === sort.field;
  return (
    <th scope="col" aria-sort={selected ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      className="py-2.5 px-4 text-left font-medium text-muted-foreground">
      <button type="button" onClick={() => sort.toggle(column)} className="hover:text-foreground focus-visible:outline focus-visible:outline-2 rounded">
        {label} <span aria-hidden="true">{selected ? sort.direction === "asc" ? "↑" : "↓" : "↕"}</span>
      </button>
    </th>
  );
}
